import { useNotify } from 'react-admin';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useRecordContext } from 'react-admin';
import { MemberId, MissionSummary, MissionSessionId } from '@wwf/sdk';
import { removeSessionParticipant } from '@wwf/sdk';
import { missionSummaryGetOneQueryKey } from '@domain/mission';

interface MissionParticipantBulkDeleteButtonProps {
    selectedIds: MemberId[];
    onUnselectItems: () => void;
    sessionId: MissionSessionId;
    explanation?: string | null;
}

export const MissionParticipantBulkDeleteButton = ({
    selectedIds,
    onUnselectItems,
    sessionId,
    explanation,
}: MissionParticipantBulkDeleteButtonProps) => {
    const record = useRecordContext<MissionSummary>();
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { mutate: deleteParticipants, isPending } = useMutation({
        mutationKey: [
            'missionParticipant',
            'deleteMissionParticipants',
            { missionId: record?.id, session_id: sessionId },
        ],
        mutationFn: async () => {
            for (const selectedMemberId of selectedIds) {
                await removeSessionParticipant(
                    record!.id,
                    sessionId,
                    selectedMemberId,
                    explanation
                );
            }
        },
        onSuccess: async () => {
            notify('Participants removed', { type: 'success' });
            onUnselectItems();
            if (record?.id) {
                await queryClient.invalidateQueries({
                    queryKey: missionSummaryGetOneQueryKey(record.id),
                });
            }
        },
        onError: (error: any) => {
            notify(`Error removing participants: ${error.message}`, {
                type: 'error',
            });
        },
    });

    const handleClick = () => {
        if (!record?.id) {
            notify('No mission ID found', { type: 'error' });
            return;
        }

        deleteParticipants();
    };

    return (
        <Button
            onClick={handleClick}
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
            disabled={isPending}
        >
            {isPending ? 'Removing...' : 'Remove Participants'}
        </Button>
    );
};
